import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import { vendorPlants } from '../../data/mockData';
import { PlantStatus, TicketStatus } from '../../types';
import { relativeTime } from '../../utils/dateHelpers';

export default function VendorPlantList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const filtered = vendorPlants
    .filter(p =>
      p.id.toLowerCase().includes(search.toLowerCase()) ||
      p.consumerName.toLowerCase().includes(search.toLowerCase()) ||
      p.state.toLowerCase().includes(search.toLowerCase())
    )
    .slice(0, 50);

  const getStatusType = (status: PlantStatus): 'online' | 'offline' | 'warning' | 'inactive' => {
    if (status === PlantStatus.ONLINE) return 'online';
    if (status === PlantStatus.OFFLINE) return 'offline';
    if (status === PlantStatus.WARNING || status === PlantStatus.FAULT) return 'warning';
    return 'inactive';
  };

  const getTicketStatusType = (status: TicketStatus | null) => {
    if (!status) return null;
    if (status === TicketStatus.OPEN) return 'open';
    if (status === TicketStatus.IN_PROGRESS) return 'in_progress';
    return 'resolved';
  };

  return (
    <div className="p-6 space-y-4">
      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-3">
          <SectionDivider title="Plant List" subtitle={`${filtered.length} plants shown (of ${vendorPlants.length} total)`} />
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              type="text"
              placeholder="Search plants…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-xs border border-border rounded-input pl-7 pr-3 py-1.5 bg-white focus:outline-none focus:border-accent w-52"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2.5 text-text-tertiary font-medium">Plant ID</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Consumer</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">State</th>
                <th className="text-left py-2.5 text-text-tertiary font-medium">Inverter</th>
                <th className="text-center py-2.5 text-text-tertiary font-medium">Status</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">kWh Today</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">PR%</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Last Data</th>
                <th className="text-right py-2.5 text-text-tertiary font-medium">Alarms</th>
                <th className="text-center py-2.5 text-text-tertiary font-medium">Ticket</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(plant => {
                const isUnderperformer = plant.prPercent < 70;
                const ticketStatusType = getTicketStatusType(plant.ticketStatus);
                return (
                  <tr
                    key={plant.id}
                    onClick={() => navigate('/consumer/dashboard')}
                    className={`border-b border-border/50 cursor-pointer hover:bg-bg-secondary transition-colors ${isUnderperformer ? 'bg-orange-50/50' : ''}`}
                  >
                    <td className="py-2.5 font-mono font-medium text-accent">{plant.id}</td>
                    <td className="py-2.5 text-text-primary">{plant.consumerName}</td>
                    <td className="py-2.5 text-text-secondary">{plant.state}</td>
                    <td className="py-2.5 text-text-secondary">{plant.inverterBrand}</td>
                    <td className="py-2.5 text-center">
                      <StatusPill status={getStatusType(plant.status)} />
                    </td>
                    <td className="py-2.5 text-right font-medium text-text-primary">{plant.todayKWh}</td>
                    <td className={`py-2.5 text-right font-semibold ${plant.prPercent < 70 ? 'text-warning' : plant.prPercent >= 80 ? 'text-success' : 'text-text-primary'}`}>
                      {plant.prPercent}%
                    </td>
                    <td className="py-2.5 text-right text-text-tertiary">{relativeTime(plant.lastSeen)}</td>
                    <td className={`py-2.5 text-right ${plant.activeAlarms > 0 ? 'text-warning font-semibold' : 'text-text-secondary'}`}>
                      {plant.activeAlarms || '—'}
                    </td>
                    <td className="py-2.5 text-center">
                      {ticketStatusType ? <StatusPill status={ticketStatusType} /> : <span className="text-text-tertiary">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-text-tertiary mt-3 text-center">Showing 50 of {vendorPlants.length} plants. Use filters to narrow results.</p>
      </div>
    </div>
  );
}
